import { useState, useCallback, useRef, useEffect } from 'react';
import { CameraCapture, CameraOptions, CapturedPhoto } from '../types/submission-workflow';

type ZoomableTrackConstraints = MediaTrackConstraints & {
  advanced?: Array<MediaTrackConstraintSet & { zoom?: number }>;
};

type ZoomTrackCapabilities = MediaTrackCapabilities & {
  zoom?: { min?: number; max?: number; step?: number } | number;
};

type ZoomTrackSettings = MediaTrackSettings & {
  zoom?: number;
};

const DEFAULT_CAMERA_OPTIONS: CameraOptions = {
  facingMode: 'environment', // Prefer back camera for items
  quality: 0.8,
  maxWidth: 1920,
  maxHeight: 1080,
  initialZoom: 1
};

export const useCameraCapture = (options: Partial<CameraOptions> = {}) => {
  const cameraOptions = { ...DEFAULT_CAMERA_OPTIONS, ...options };

  const [camera, setCamera] = useState<CameraCapture>({
    stream: null,
    isActive: false,
    hasPermission: false,
    error: undefined,
    facingMode: cameraOptions.facingMode,
    zoom: cameraOptions.initialZoom ?? 1,
    minZoom: 1,
    maxZoom: 1,
    zoomStep: 0.1,
    supportsZoom: false
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const zoomConfigRef = useRef({
    min: 1,
    max: 1,
    step: 0.1,
    supportsZoom: false
  });
  const zoomValueRef = useRef(cameraOptions.initialZoom ?? 1);

  const setZoom = useCallback(async (requestedZoom: number) => {
    const track = videoTrackRef.current;
    const config = zoomConfigRef.current;

    if (!track || !config.supportsZoom) {
      const fallbackZoom = config.supportsZoom
        ? Math.min(Math.max(requestedZoom, config.min), config.max)
        : 1;
      zoomValueRef.current = fallbackZoom;
      setCamera(prev => ({ ...prev, zoom: fallbackZoom }));
      return;
    }

    const clampedZoom = Math.min(Math.max(requestedZoom, config.min), config.max);

    try {
      const zoomConstraint: ZoomableTrackConstraints = {
        advanced: [{ zoom: clampedZoom }]
      };
      await track.applyConstraints(zoomConstraint);
      zoomValueRef.current = clampedZoom;
      setCamera(prev => ({ ...prev, zoom: clampedZoom }));
    } catch (error) {
      console.warn('Failed to set camera zoom:', error);
    }
  }, []);

  const configureZoomForTrack = useCallback(async (track: MediaStreamTrack | null) => {
    if (!track) {
      zoomConfigRef.current = {
        min: 1,
        max: 1,
        step: 0.1,
        supportsZoom: false
      };
      zoomValueRef.current = cameraOptions.initialZoom ?? 1;
      setCamera(prev => ({
        ...prev,
        supportsZoom: false,
        zoom: zoomValueRef.current,
        minZoom: 1,
        maxZoom: 1,
        zoomStep: 0.1
      }));
      return;
    }

    const capabilities = typeof track.getCapabilities === 'function'
      ? (track.getCapabilities() as ZoomTrackCapabilities)
      : ({} as ZoomTrackCapabilities);

    const settings = typeof track.getSettings === 'function'
      ? (track.getSettings() as ZoomTrackSettings)
      : ({} as ZoomTrackSettings);

    const zoomCapability = capabilities?.zoom;

    let minZoom = 1;
    let maxZoom = 1;
    let zoomStep = 0.1;
    let supportsZoom = false;

    if (zoomCapability !== undefined) {
      if (typeof zoomCapability === 'object') {
        minZoom = typeof zoomCapability.min === 'number' ? zoomCapability.min : minZoom;
        maxZoom = typeof zoomCapability.max === 'number' ? zoomCapability.max : maxZoom;
        zoomStep = typeof zoomCapability.step === 'number' && zoomCapability.step > 0 ? zoomCapability.step : zoomStep;
        supportsZoom = maxZoom > minZoom;
      } else if (typeof zoomCapability === 'number') {
        minZoom = zoomCapability;
        maxZoom = zoomCapability;
        zoomStep = 0;
        supportsZoom = false;
      }
    }

    zoomConfigRef.current = {
      min: minZoom,
      max: maxZoom,
      step: zoomStep || 0.1,
      supportsZoom
    };

    if (supportsZoom) {
      const desiredZoom = cameraOptions.initialZoom ?? settings.zoom ?? minZoom;
      const clampedZoom = Math.min(Math.max(desiredZoom, minZoom), maxZoom);
      await setZoom(clampedZoom);
      setCamera(prev => ({
        ...prev,
        supportsZoom: true,
        minZoom,
        maxZoom,
        zoomStep: zoomStep || 0.1
      }));
    } else {
      zoomValueRef.current = 1;
      setCamera(prev => ({
        ...prev,
        supportsZoom: false,
        zoom: 1,
        minZoom: 1,
        maxZoom: 1,
        zoomStep: 0.1
      }));
    }
  }, [cameraOptions.initialZoom, setZoom]);

  const zoomIn = useCallback(async () => {
    const { step, max, supportsZoom } = zoomConfigRef.current;
    if (!supportsZoom) return;
    const nextZoom = zoomValueRef.current + (step || 0.1);
    await setZoom(Math.min(nextZoom, max));
  }, [setZoom]);

  const zoomOut = useCallback(async () => {
    const { step, min, supportsZoom } = zoomConfigRef.current;
    if (!supportsZoom) return;
    const nextZoom = zoomValueRef.current - (step || 0.1);
    await setZoom(Math.max(nextZoom, min));
  }, [setZoom]);

  // Check if camera is supported
  const isCameraSupported = useCallback(() => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }, []);

  // Request camera permission and start stream
  const startCamera = useCallback(async () => {
    if (!isCameraSupported()) {
      setCamera(prev => ({
        ...prev,
        error: 'Camera is not supported on this device'
      }));
      return false;
    }

    try {
      setCamera(prev => ({ ...prev, error: undefined }));

      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: cameraOptions.facingMode,
          width: { ideal: cameraOptions.maxWidth },
          height: { ideal: cameraOptions.maxHeight }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playError) {
          // Ignore AbortError as it's usually caused by rapid re-renders or camera switching
          if (playError instanceof Error && playError.name !== 'AbortError') {
            console.warn('Video play error:', playError);
          }
        }
      }

      // Store stream in ref for cleanup
      streamRef.current = stream;
      const [videoTrack] = stream.getVideoTracks();
      videoTrackRef.current = videoTrack ?? null;
      await configureZoomForTrack(videoTrack ?? null);

      setCamera(prev => ({
        ...prev,
        stream,
        isActive: true,
        hasPermission: true
      }));

      return true;
    } catch (error) {
      let errorMessage = 'Failed to access camera';

      if (error instanceof Error) {
        switch (error.name) {
          case 'NotAllowedError':
            errorMessage = 'Camera permission denied. Please allow camera access and try again.';
            break;
          case 'NotFoundError':
            errorMessage = 'No camera found on this device.';
            break;
          case 'NotReadableError':
            errorMessage = 'Camera is already in use by another application.';
            break;
          case 'OverconstrainedError':
            errorMessage = 'Camera does not support the requested configuration.';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      setCamera(prev => ({
        ...prev,
        error: errorMessage,
        isActive: false,
        hasPermission: false
      }));

      return false;
    }
  }, [cameraOptions, configureZoomForTrack, isCameraSupported]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      // Pause video first to prevent play() conflicts
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    videoTrackRef.current = null;
    zoomConfigRef.current = {
      min: 1,
      max: 1,
      step: 0.1,
      supportsZoom: false
    };
    zoomValueRef.current = cameraOptions.initialZoom ?? 1;

    setCamera(prev => ({
      ...prev,
      stream: null,
      isActive: false,
      zoom: zoomValueRef.current,
      supportsZoom: false,
      minZoom: 1,
      maxZoom: 1,
      zoomStep: 0.1
    }));
  }, [cameraOptions.initialZoom]); // No dependencies - uses refs instead

  // Switch between front and back camera
  const switchCamera = useCallback(async () => {
    const newFacingMode = camera.facingMode === 'environment' ? 'user' : 'environment';

    // Stop current stream
    stopCamera();

    // Small delay to ensure stream is fully stopped
    await new Promise(resolve => setTimeout(resolve, 100));

    // Update facing mode and restart
    setCamera(prev => ({ ...prev, facingMode: newFacingMode }));

    // Restart with new facing mode
    const updatedOptions = { ...cameraOptions, facingMode: newFacingMode };

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: newFacingMode,
          width: { ideal: updatedOptions.maxWidth },
          height: { ideal: updatedOptions.maxHeight }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playError) {
          // Ignore AbortError as it's usually caused by rapid re-renders
          if (playError instanceof Error && playError.name !== 'AbortError') {
            console.warn('Video play error:', playError);
          }
        }
      }

      // Store stream in ref for cleanup
      streamRef.current = stream;
      const [videoTrack] = stream.getVideoTracks();
      videoTrackRef.current = videoTrack ?? null;
      await configureZoomForTrack(videoTrack ?? null);

      setCamera(prev => ({
        ...prev,
        stream,
        isActive: true,
        facingMode: newFacingMode
      }));
    } catch {
      setCamera(prev => ({
        ...prev,
        error: 'Failed to switch camera',
        isActive: false
      }));
    }
  }, [camera.facingMode, cameraOptions, stopCamera, configureZoomForTrack]);

  // Capture photo from video stream
  const capturePhoto = useCallback(async (): Promise<CapturedPhoto | null> => {
    if (!camera.isActive || !camera.stream || !videoRef.current || !canvasRef.current) {
      return null;
    }

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Failed to get canvas context');
      }

      // Ensure we have valid video dimensions before drawing
      const videoWidth = video.videoWidth || canvas.width;
      const videoHeight = video.videoHeight || canvas.height;
      const squareSize = Math.min(videoWidth, videoHeight);
      const offsetX = (videoWidth - squareSize) / 2;
      const offsetY = (videoHeight - squareSize) / 2;

      // Set canvas to square for consistent output
      canvas.width = squareSize;
      canvas.height = squareSize;

      // Draw centered square crop from the video frame
      context.drawImage(
        video,
        offsetX,
        offsetY,
        squareSize,
        squareSize,
        0,
        0,
        squareSize,
        squareSize
      );

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          cameraOptions.quality
        );
      });

      // Create captured photo object
      const timestamp = Date.now();
      const filename = `camera_capture_${timestamp}.jpg`;
      const url = URL.createObjectURL(blob);

      const capturedPhoto: CapturedPhoto = {
        id: `photo_${timestamp}_${Math.random().toString(36).substring(2)}`,
        filename,
        url,
        size: blob.size,
        dimensions: {
          width: canvas.width,
          height: canvas.height
        },
        source: {
          type: 'camera',
          timestamp: new Date()
        },
        capturedAt: new Date(),
        isUploaded: false
      };

      return capturedPhoto;
    } catch (error) {
      console.error('Error capturing photo:', error);
      setCamera(prev => ({
        ...prev,
        error: 'Failed to capture photo'
      }));
      return null;
    }
  }, [camera.isActive, camera.stream, cameraOptions.quality]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Direct cleanup without depending on stopCamera function
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    };
  }, []); // Empty dependency array - only runs on mount/unmount

  return {
    // State
    camera,
    isCameraSupported: isCameraSupported(),

    // Refs for video and canvas elements
    videoRef,
    canvasRef,

    // Actions
    startCamera,
    stopCamera,
    switchCamera,
    capturePhoto,
    setZoom,
    zoomIn,
    zoomOut,

    // Utilities
    clearError: () => setCamera(prev => ({ ...prev, error: undefined }))
  };
};
